"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Quote } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ThePowerOfWhy() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll();
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const {
        top,
        height
      } = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const start = top - windowHeight * 0.5;
      const end = height - windowHeight;
      const elementTop = sectionRef.current.offsetTop;
      const elementHeight = sectionRef.current.offsetHeight;
      const scrollY = window.scrollY;
      const viewHeight = window.innerHeight;
      const scrollDistance = scrollY + viewHeight - elementTop;
      const totalDistance = elementHeight + viewHeight;
      let percentage = 0;
      if (scrollY > elementTop - viewHeight) {
        const scrolled = scrollY - (elementTop - viewHeight * 0.5);
        const total = elementHeight - viewHeight * 0.5;
        percentage = Math.max(0, Math.min(100, scrolled / total * 100));
      }
      setProgress(percentage);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="43964" id="solving-the-uk-housing-crisis" ref={sectionRef} className="dark bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      <div className="container mx-auto px-6 relative">
        <div className="grid grid-cols-1 lg:grid-cols-[120px_1fr] gap-12 lg:gap-24">
          {}
          <div className="hidden lg:block relative">
            <div className="sticky top-1/2 -translate-y-1/2 h-[60vh] w-px bg-border mx-auto">
              <div className="absolute top-0 left-0 w-full bg-primary transition-all duration-100 ease-out" style={{
              height: `${progress}%`
            }} />
            </div>
            <div className="sticky top-[80%] left-0 w-full text-center mt-4">
              <span className="text-xs font-mono text-primary tracking-widest rotate-90 block origin-center translate-y-8">
                {Math.round(progress)}% IMPACT
              </span>
            </div>
          </div>

          {}
          <div ref={contentRef} className={`max-w-3xl transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
            <Link to="/" className="inline-block mb-8">
              
            </Link>
            <header className="mb-16">
              <h2 className="text-sm font-default font-semibold text-primary tracking-widest uppercase mb-6">
                Social Responsibility
              </h2>
              <h3 className="text-5xl md:text-7xl font-default font-bold tracking-tighter leading-[0.9]">
                Bridging the <br />
                <span className="text-muted-foreground italic">Housing Gap</span>
              </h3>
            </header>

            <div className="space-y-12">
              <p className="text-2xl md:text-[28px] leading-relaxed font-serif text-foreground/90 font-light">
                The United Kingdom is facing an unprecedented housing shortage, with demand far outstripping supply. At YieldCapital, we don't just see a market gap; we see a moral imperative to provide high-quality homes for those who need them most while delivering institutional-grade returns.
              </p>

              <div className="relative py-12 my-16 -mx-6 md:-mx-12 lg:-mx-24">
                <Quote className="absolute top-0 left-6 md:left-12 lg:left-24 size-12 text-primary/20 -scale-x-100" />
                <blockquote className="relative z-10 text-7xl md:text-[120px] font-default font-bold tracking-tighter leading-none text-primary text-center px-4">
                  1.1M <span className="block text-2xl md:text-3xl text-foreground mt-4"><br /></span>
                </blockquote>
              </div>

              <p className="text-xl md:text-2xl leading-relaxed font-serif text-muted-foreground">
                Our philosophy is rooted in "Profit with Purpose." By partnering with housing associations and local councils, we create a sustainable ecosystem where capital serves a dual purpose: financial security for our investors and housing stability for the community. This isn't just property; it's social infrastructure.
              </p>

              <p className="text-xl md:text-2xl leading-relaxed font-serif text-muted-foreground">
                We believe that true sophistication lies in the resolution of complexity. Through rigorous asset selection and an ESG-led strategy, we craft investment vehicles that feel inevitable. By anchoring every decision in social impact, we eliminate the superfluous and elevate the essential.
              </p>

              <div className="pt-12 flex flex-col sm:flex-row gap-6 items-start sm:items-center border-t border-border mt-12">
                <div className="flex-1">
                  <h4 className="font-default text-xl font-bold mb-2">Ready to invest with purpose?</h4>
                  <p className="text-muted-foreground font-serif">Join us in solving the UK's most pressing property challenge.</p>
                </div>
                <Button asChild size="lg" className="rounded-full px-8 text-base border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-lg shadow-primary/10">
                  <Link to="https://10percent-yield.com/investments/">
                    View Our Investments <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}